import React, { useState, useEffect } from 'react';
import {
  Container,
  Heading,
  Box,
  Flex,
  SimpleGrid,
  Button,
  Text,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Alert,
  AlertIcon,
  useToast,
  Spinner,
  useColorModeValue,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  HStack,
  VStack,
  Divider,
} from '@chakra-ui/react';
import { FaUser, FaCoins, FaHistory, FaMoneyBillWave } from 'react-icons/fa';
import { useWallet } from '../context/WalletContext';
import StatsCard from '../components/StatsCard';
import TransactionList from '../components/TransactionList';
import blindboxService from '../services/blindboxService';
import liquidityService from '../services/liquidityService';

const DashboardPage: React.FC = () => {
  const { walletAddress, userBalance, addBalance, refreshBalance } = useWallet();
  const [isLoading, setIsLoading] = useState(false);
  const [addAmount, setAddAmount] = useState<string>('10');
  const [stats, setStats] = useState<any>(null);
  const [userProvider, setUserProvider] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (walletAddress) {
      fetchData();
    }
  }, [walletAddress]);

  const fetchData = async () => {
    if (!walletAddress) return;

    setIsLoading(true);
    try {
      // Get blindbox stats
      const blindboxStats = await blindboxService.getBlindboxStats();
      setStats(blindboxStats);
      setTransactions(blindboxStats.recentTransactions);

      // Check if user is a liquidity provider
      try {
        const providerData = await liquidityService.getProvider(walletAddress);
        setUserProvider(providerData.provider);
      } catch (err) {
        // User is not a provider, that's fine
        setUserProvider(null);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBalance = async () => {
    const amount = parseFloat(addAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Invalid amount',
        description: 'Please enter a valid amount.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      await addBalance(amount);
      toast({
        title: 'Success',
        description: `Added ${amount} SOL to your wallet.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Error adding balance:', err);
      toast({
        title: 'Error',
        description: 'Failed to add balance.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!walletAddress) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          Please connect your wallet to view the dashboard.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Heading as="h1" mb={6}>
        Dashboard
      </Heading>

      {/* User Stats */}
      <Box mb={8}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <StatsCard
            title="Wallet Address"
            stat={`${walletAddress.substring(0, 6)}...${walletAddress.substring(walletAddress.length - 4)}`}
            icon={<FaUser size="3em" />}
          />
          
          <StatsCard
            title="Balance"
            stat={`${userBalance.toFixed(2)} SOL`}
            icon={<FaCoins size="3em" />}
          />
          
          <StatsCard
            title="Liquidity Provided"
            stat={userProvider ? `${userProvider.amount.toFixed(2)} SOL` : '0 SOL'}
            helpText={userProvider ? `${userProvider.rewards.toFixed(2)} SOL rewards` : 'Not a provider'}
            icon={<FaMoneyBillWave size="3em" />}
          />
        </SimpleGrid>
      </Box>

      {/* Demo Tools and Transactions */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
        {/* Demo Tools */}
        <Box 
          bg={cardBg} 
          p={6} 
          borderRadius="lg" 
          boxShadow="sm"
          height="fit-content"
        >
          <Heading size="md" mb={4}>
            Demo Tools
          </Heading>
          <Text color="gray.500" mb={4}>
            Add SOL to your wallet for demonstration purposes.
          </Text>
          
          <FormControl mb={4}>
            <FormLabel>Amount (SOL)</FormLabel>
            <Input
              type="number"
              value={addAmount}
              onChange={(e) => setAddAmount(e.target.value)}
              placeholder="Enter amount in SOL"
              disabled={isLoading}
            />
            <FormHelperText>
              This is for demo purposes only and doesn't represent real SOL.
            </FormHelperText>
          </FormControl>
          
          <Button
            colorScheme="brand"
            isLoading={isLoading}
            onClick={handleAddBalance}
            w="full"
            mb={4}
          >
            Add SOL to Wallet
          </Button>
          
          <Divider my={4} />
          
          <Heading size="sm" mb={4}>
            Quick Actions
          </Heading>
          
          <Stack spacing={3}>
            <Button 
              as="a" 
              href="/blindbox" 
              variant="outline"
              colorScheme="brand"
              leftIcon={<FaCoins />}
            >
              Open BlindBox
            </Button>
            
            <Button 
              as="a" 
              href="/liquidity" 
              variant="outline"
              colorScheme="brand"
              leftIcon={<FaMoneyBillWave />}
            >
              Manage Liquidity
            </Button>
          </Stack>
        </Box>
        
        {/* Transaction History */}
        <Box>
          {isLoading ? (
            <Flex justify="center" p={8}>
              <Spinner />
            </Flex>
          ) : (
            <TransactionList 
              transactions={transactions.filter(
                (tx) => tx.address === walletAddress
              )} 
              title="Your Transaction History"
            />
          )}
          
          {transactions.filter((tx) => tx.address === walletAddress).length === 0 && (
            <Alert status="info" borderRadius="md" mt={4}>
              <AlertIcon />
              No transactions found. Try opening a BlindBox or providing liquidity!
            </Alert>
          )}
        </Box>
      </SimpleGrid>
      
      {/* System Stats */}
      {stats && (
        <Box mt={8}>
          <Heading size="md" mb={4}>
            System Stats
          </Heading>
          
          <Box 
            bg={cardBg} 
            p={6} 
            borderRadius="lg" 
            boxShadow="sm"
          >
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
              <Stat>
                <StatLabel>Pool Size</StatLabel>
                <StatNumber>{stats.poolInfo.totalPoolSize.toFixed(2)} SOL</StatNumber>
                <StatHelpText>
                  <StatArrow type={stats.poolInfo.growth >= 0 ? 'increase' : 'decrease'} />
                  {stats.poolInfo.growth.toFixed(2)} SOL
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Expected Value</StatLabel>
                <StatNumber>
                  {stats.stats.currentMode === 'Normal' 
                    ? stats.stats.normalExpectedValue.toFixed(2) 
                    : stats.stats.adjustedExpectedValue.toFixed(2)} SOL
                </StatNumber>
                <StatHelpText>
                  Current Mode: <Badge colorScheme={stats.poolInfo.state === 'Normal' ? 'green' : 'orange'}>
                    {stats.poolInfo.state}
                  </Badge>
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Providers</StatLabel>
                <StatNumber>{stats.poolInfo.totalProviders}</StatNumber>
                <StatHelpText>
                  Reward Pool: {stats.poolInfo.rewardPool.toFixed(2)} SOL
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>System Fee</StatLabel>
                <StatNumber>{stats.stats.systemFeePercentage}%</StatNumber>
                <StatHelpText>
                  Collected: {stats.poolInfo.totalSystemFees.toFixed(2)} SOL
                </StatHelpText>
              </Stat>
            </SimpleGrid>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default DashboardPage; 